import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EnsueCollection } from "../../../../../../ensue-react-system/utilities/ensue-collection";
import { FootballTournamentManager } from "../../../../../../commons/models/football/football-tournament-manager";
import { StatusEnum } from "../../../../../../commons/enums/status-enum";
import { manageFootballTeamService } from "../../../../../../commons";

export interface IHostedTournamentManagerState {
  hostedTournamentManagers: EnsueCollection<FootballTournamentManager>;
  hostedTournamentManagerFetchStatus: StatusEnum;
  addhostedTournamentManagerStatus: StatusEnum;
  removehostedTournamentManagerStatus: StatusEnum;
}

const initialState: IHostedTournamentManagerState = {
  hostedTournamentManagers: new EnsueCollection<FootballTournamentManager>(),
  hostedTournamentManagerFetchStatus: StatusEnum.Idle,
  addhostedTournamentManagerStatus: StatusEnum.Idle,
  removehostedTournamentManagerStatus: StatusEnum.Idle,
};

interface IHostedTournamentManagerAddRemoveParams {
  teamId: number;
  hostedTournamentId: number;
  userId: number;
  remove?: boolean;
}

interface IHostedTournamentManagerListParams {
  teamId: number;
  hostedTournamentId: number;
}

export const fetchHostedTournamentManager = createAsyncThunk<
  EnsueCollection<FootballTournamentManager>,
  IHostedTournamentManagerListParams,
  {
    rejectValue: unknown;
  }
>("hostedTournamentManager/fetch", async (options: IHostedTournamentManagerListParams, { rejectWithValue }) => {
  try {
    return await manageFootballTeamService.getHostedTournamentManagers(options.teamId, options.hostedTournamentId);
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const addRemoveHostedTournamentManager = createAsyncThunk<
  FootballTournamentManager,
  IHostedTournamentManagerAddRemoveParams,
  {
    rejectValue: unknown;
  }
>("hostedTournamentManager/addRemove", async (params: IHostedTournamentManagerAddRemoveParams, { rejectWithValue }) => {
  try {
    return await manageFootballTeamService.addRemoveHostedTournamentManager(
      params.teamId,
      params.hostedTournamentId,
      params.userId,
      params.remove
    );
  } catch (e) {
    return rejectWithValue(e);
  }
});

const footballHostedTournamentManagerSlice = createSlice({
  name: "hostedTournamentManager",
  initialState: initialState,
  reducers: {
    resetHostedTournamentManagerState(state) {
      state.hostedTournamentManagers = new EnsueCollection<FootballTournamentManager>();
      state.hostedTournamentManagerFetchStatus = StatusEnum.Idle;
      state.addhostedTournamentManagerStatus = StatusEnum.Idle;
      state.removehostedTournamentManagerStatus = StatusEnum.Idle;
    },
    resetHostedTournamentManagerAddRemoveStatus(state) {
      state.addhostedTournamentManagerStatus = StatusEnum.Idle;
      state.removehostedTournamentManagerStatus = StatusEnum.Idle;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchHostedTournamentManager.pending, (state) => {
        state.hostedTournamentManagerFetchStatus = StatusEnum.Loading;
      })
      .addCase(
        fetchHostedTournamentManager.fulfilled,
        (state, action: PayloadAction<EnsueCollection<FootballTournamentManager>>) => {
          state.hostedTournamentManagerFetchStatus = StatusEnum.Succeeded;
          state.hostedTournamentManagers = action.payload;
        }
      )
      .addCase(fetchHostedTournamentManager.rejected, (state) => {
        state.hostedTournamentManagerFetchStatus = StatusEnum.Failed;
      })

      // Add/Remove manager
      .addCase(addRemoveHostedTournamentManager.pending, (state) => {
        state.addhostedTournamentManagerStatus = StatusEnum.Loading;
      })
      .addCase(addRemoveHostedTournamentManager.fulfilled, (state) => {
        state.addhostedTournamentManagerStatus = StatusEnum.Succeeded;
      })
      .addCase(addRemoveHostedTournamentManager.rejected, (state) => {
        state.addhostedTournamentManagerStatus = StatusEnum.Failed;
      });
  },
});

export const { resetHostedTournamentManagerState, resetHostedTournamentManagerAddRemoveStatus } =
  footballHostedTournamentManagerSlice.actions;
export default footballHostedTournamentManagerSlice.reducer;
